import { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PILLAR_TEMPLATES } from '../PillarTemplate';

/**
 * Connection types for architecture diagram edges
 */
export const CONNECTION_TYPES = {
  DATA_FLOW: { key: 'DATA_FLOW', label: 'Data Flow', color: '#3b82f6', style: 'solid', hasArrow: true },
  TRUST_CONTROL: { key: 'TRUST_CONTROL', label: 'Trust/Control', color: '#10b981', style: 'solid', hasArrow: true },
  INTEGRATION: { key: 'INTEGRATION', label: 'Integration', color: '#f59e0b', style: 'dashed', hasArrow: false }
};

/**
 * Node categories for grouping
 */
export const NODE_CATEGORIES = {
  INPUT: 'input',
  TECH: 'tech',
  OUTPUT: 'output'
};

/**
 * Generate a unique node ID
 */
function generateNodeId() {
  return `node-${uuidv4().slice(0, 8)}`;
}

/**
 * Generate a unique edge ID
 */
function generateEdgeId() {
  return `edge-${uuidv4().slice(0, 8)}`;
}

/**
 * Hook for managing architecture diagram state
 *
 * @param {string} initialPillarCode - Initial pillar to display (default: 'IDENTITY')
 * @param {Object} productAdoptions - Product adoption data from useCisaAssessment
 * @returns {Object} State and actions for the architecture diagram
 */
export function useArchitectureState(initialPillarCode = 'IDENTITY', productAdoptions = {}) {
  // Currently selected pillar
  const [selectedPillar, setSelectedPillar] = useState(initialPillarCode);

  // Nodes per pillar: { pillarCode: [node, node, ...] }
  const [nodesByPillar, setNodesByPillar] = useState(() => {
    const initial = {};
    Object.keys(PILLAR_TEMPLATES).forEach(pillarCode => {
      initial[pillarCode] = PILLAR_TEMPLATES[pillarCode]?.nodes || [];
    });
    return initial;
  });

  // Edges per pillar: { pillarCode: [edge, edge, ...] }
  const [edgesByPillar, setEdgesByPillar] = useState(() => {
    const initial = {};
    Object.keys(PILLAR_TEMPLATES).forEach(pillarCode => {
      initial[pillarCode] = PILLAR_TEMPLATES[pillarCode]?.edges || [];
    });
    return initial;
  });

  // Viewport state: pan and zoom
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

  // Selected node/edge for editing
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);

  // Undo stack for deleted nodes (stores last 5 deletions)
  const [deletedNodes, setDeletedNodes] = useState([]);

  /**
   * Get nodes for the current pillar
   */
  const nodes = useMemo(() => {
    return nodesByPillar[selectedPillar] || [];
  }, [nodesByPillar, selectedPillar]);

  /**
   * Get edges for the current pillar
   */
  const edges = useMemo(() => {
    return edgesByPillar[selectedPillar] || [];
  }, [edgesByPillar, selectedPillar]);

  /**
   * Get node by ID
   */
  const getNodeById = useCallback((nodeId) => {
    return nodes.find(n => n.id === nodeId);
  }, [nodes]);

  /**
   * Add a new node to the current pillar
   */
  const addNode = useCallback((nodeData) => {
    const newNode = {
      id: generateNodeId(),
      pillarCode: selectedPillar,
      label: nodeData.label || 'New Node',
      vendorName: nodeData.vendorName || null,
      productName: nodeData.productName || null,
      category: nodeData.category || 'tech',
      x: nodeData.x ?? 400,
      y: nodeData.y ?? 300,
      adoptionStatus: nodeData.adoptionStatus || null,
      isInput: nodeData.isInput || false,
      isOutput: nodeData.isOutput || false,
      locked: nodeData.locked || false,
      icon: nodeData.icon || null
    };

    setNodesByPillar(prev => ({
      ...prev,
      [selectedPillar]: [...(prev[selectedPillar] || []), newNode]
    }));

    return newNode.id;
  }, [selectedPillar]);

  /**
   * Update node position (after drag)
   */
  const moveNode = useCallback((nodeId, x, y) => {
    setNodesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).map(node =>
        node.id === nodeId ? { ...node, x, y } : node
      )
    }));
  }, [selectedPillar]);

  /**
   * Update node properties
   */
  const updateNode = useCallback((nodeId, updates) => {
    setNodesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).map(node =>
        node.id === nodeId ? { ...node, ...updates } : node
      )
    }));
  }, [selectedPillar]);

  /**
   * Delete a node and its connected edges (with undo support)
   */
  const deleteNode = useCallback((nodeId) => {
    // Find the node to delete and save it for undo
    const nodeToDelete = (nodesByPillar[selectedPillar] || []).find(n => n.id === nodeId);
    const connectedEdges = (edgesByPillar[selectedPillar] || []).filter(
      edge => edge.sourceId === nodeId || edge.targetId === nodeId
    );

    if (nodeToDelete) {
      setDeletedNodes(prev => [
        { node: nodeToDelete, edges: connectedEdges, pillar: selectedPillar },
        ...prev.slice(0, 4) // Keep last 5
      ]);
    }

    setNodesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).filter(node => node.id !== nodeId)
    }));

    // Also remove edges connected to this node
    setEdgesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).filter(
        edge => edge.sourceId !== nodeId && edge.targetId !== nodeId
      )
    }));

    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
    }
  }, [selectedPillar, selectedNodeId, nodesByPillar, edgesByPillar]);

  /**
   * Undo the last node deletion
   */
  const undoDelete = useCallback(() => {
    if (deletedNodes.length === 0) return null;

    const [lastDeleted, ...remaining] = deletedNodes;
    setDeletedNodes(remaining);

    // Restore the node
    setNodesByPillar(prev => ({
      ...prev,
      [lastDeleted.pillar]: [...(prev[lastDeleted.pillar] || []), lastDeleted.node]
    }));

    // Restore the edges
    if (lastDeleted.edges.length > 0) {
      setEdgesByPillar(prev => ({
        ...prev,
        [lastDeleted.pillar]: [...(prev[lastDeleted.pillar] || []), ...lastDeleted.edges]
      }));
    }

    return lastDeleted.node;
  }, [deletedNodes]);

  /**
   * Check if undo is available
   */
  const canUndo = deletedNodes.length > 0;

  /**
   * Add a new edge between nodes
   */
  const addEdge = useCallback((sourceId, targetId, type = 'DATA_FLOW', label = '') => {
    // Check if edge already exists
    const existing = (edgesByPillar[selectedPillar] || []).find(
      e => e.sourceId === sourceId && e.targetId === targetId
    );
    if (existing) return null;

    const newEdge = {
      id: generateEdgeId(),
      sourceId,
      targetId,
      type,
      label,
      animated: false
    };

    setEdgesByPillar(prev => ({
      ...prev,
      [selectedPillar]: [...(prev[selectedPillar] || []), newEdge]
    }));

    return newEdge.id;
  }, [selectedPillar, edgesByPillar]);

  /**
   * Update edge properties
   */
  const updateEdge = useCallback((edgeId, updates) => {
    setEdgesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).map(edge =>
        edge.id === edgeId ? { ...edge, ...updates } : edge
      )
    }));
  }, [selectedPillar]);

  /**
   * Delete an edge
   */
  const deleteEdge = useCallback((edgeId) => {
    setEdgesByPillar(prev => ({
      ...prev,
      [selectedPillar]: (prev[selectedPillar] || []).filter(edge => edge.id !== edgeId)
    }));

    if (selectedEdgeId === edgeId) {
      setSelectedEdgeId(null);
    }
  }, [selectedPillar, selectedEdgeId]);

  /**
   * Switch to a different pillar
   */
  const switchPillar = useCallback((pillarCode) => {
    setSelectedPillar(pillarCode);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    // Reset viewport when switching pillars
    setViewport({ x: 0, y: 0, zoom: 1 });
  }, []);

  /**
   * Reset pillar to template defaults
   */
  const resetPillar = useCallback((pillarCode = selectedPillar) => {
    const template = PILLAR_TEMPLATES[pillarCode];
    if (!template) return;

    setNodesByPillar(prev => ({
      ...prev,
      [pillarCode]: template.nodes || []
    }));

    setEdgesByPillar(prev => ({
      ...prev,
      [pillarCode]: template.edges || []
    }));

    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, [selectedPillar]);

  /**
   * Pan the viewport
   */
  const pan = useCallback((dx, dy) => {
    setViewport(prev => ({
      ...prev,
      x: prev.x + dx,
      y: prev.y + dy
    }));
  }, []);

  /**
   * Zoom the viewport
   */
  const zoom = useCallback((newZoom) => {
    setViewport(prev => ({
      ...prev,
      zoom: Math.max(0.5, Math.min(2, newZoom))
    }));
  }, []);

  /**
   * Reset viewport to default
   */
  const resetViewport = useCallback(() => {
    setViewport({ x: 0, y: 0, zoom: 1 });
  }, []);

  /**
   * Get the full state for persistence
   */
  const getState = useCallback(() => ({
    nodesByPillar,
    edgesByPillar,
    selectedPillar,
    viewport
  }), [nodesByPillar, edgesByPillar, selectedPillar, viewport]);

  /**
   * Load state from persistence
   */
  const loadState = useCallback((state) => {
    if (state.nodesByPillar) setNodesByPillar(state.nodesByPillar);
    if (state.edgesByPillar) setEdgesByPillar(state.edgesByPillar);
    if (state.selectedPillar) setSelectedPillar(state.selectedPillar);
    if (state.viewport) setViewport(state.viewport);
  }, []);

  /**
   * Enrich nodes with adoption status from productAdoptions
   */
  const enrichedNodes = useMemo(() => {
    return nodes.map(node => {
      if (!node.productName) return node;

      // Check if this product is adopted in any function
      let adoptionStatus = null;
      Object.values(productAdoptions).forEach(products => {
        const found = (products || []).find(p =>
          p.productName.toLowerCase().includes(node.productName?.toLowerCase()) ||
          node.productName?.toLowerCase().includes(p.productName.toLowerCase())
        );
        if (found) {
          adoptionStatus = found.status;
        }
      });

      return { ...node, adoptionStatus };
    });
  }, [nodes, productAdoptions]);

  return {
    // Current pillar
    selectedPillar,
    switchPillar,

    // Nodes
    nodes: enrichedNodes,
    addNode,
    moveNode,
    updateNode,
    deleteNode,
    undoDelete,
    canUndo,
    getNodeById,

    // Edges
    edges,
    addEdge,
    updateEdge,
    deleteEdge,

    // Selection
    selectedNodeId,
    setSelectedNodeId,
    selectedEdgeId,
    setSelectedEdgeId,

    // Viewport
    viewport,
    pan,
    zoom,
    resetViewport,

    // Reset
    resetPillar,

    // Persistence
    getState,
    loadState
  };
}

export default useArchitectureState;
